import styles from './styles.scss';
import { CustomerPageOrderGroup } from 'models/CustomerPageOrderGroup';
import { CustomerPageTrackingInformation } from 'models/CustomerPageTrackingInformation';
import OrderLine from './OrderLine';
import { mapCarriageMethod } from 'components/Shared/Product/selector';
import { rm, NO_ELEMENT } from 'utils';
import { DisplayMode } from './OrderLine';
import { SupportProductModel } from 'models/SupportProductModel';

interface OwnProps {
  className?: string;
}

interface OrderLineProps extends OwnProps {
  group: CustomerPageOrderGroup;
  displayMode: DisplayMode;
  returnProductChanged?: (product: SupportProductModel) => void;
  hasTracking: boolean;
}

interface DeliveryGroupProps extends OwnProps {
  group: CustomerPageOrderGroup;
  displayMode: DisplayMode;
  returnProductChanged?: (product: SupportProductModel) => void;
}

const GetShipMentInfo = (
  allLinesInactive: boolean,
  trackingInfo?: CustomerPageTrackingInformation[]
) => {
  if (allLinesInactive) {
    return rm('customerpage_order_line_cancelled');
  }

  if (!trackingInfo) {
    return NO_ELEMENT;
  }

  // Trackinglink(s) > Shipmentdate(s)

  const trackingLinks = trackingInfo
    .filter(t => t.trackingLink && t.trackingNumber)
    .map<React.ReactNode>(t => (
      <a href={t.trackingLink} target="_blank" key={t.trackingNumber} rel="noreferrer">
        {t.trackingNumber}
      </a>
    ));

  if (trackingLinks.length > 0) {
    return (
      <span>
        <span> - {rm('orderdetails_tracking')}: </span>
        {trackingLinks.reduce((prev, curr) => [prev, ', ', curr])}
      </span>
    );
  }

  const shippingDates = trackingInfo
    .filter(t => t.shippingDate && !t.trackingLink && !t.trackingNumber)
    .map<React.ReactNode>(t => t.shippingDate);

  if (shippingDates.length > 0) {
    return (
      <span>
        <span> - {rm('orderdetails_deldate')}: </span>
        {shippingDates.reduce((prev, curr) => [prev, ', ', curr])}
      </span>
    );
  }

  return NO_ELEMENT;
};

const OrderLines = ({ displayMode, returnProductChanged, group, hasTracking }: OrderLineProps) => {
  const groupLines = group.lines.map(line => {
    const subLines =
      displayMode !== DisplayMode.Normal && line.subOrderLines ? line.subOrderLines : [];

    const subOrderLines = subLines.map(subLine => (
      <OrderLine
        key={subLine.productId}
        line={subLine}
        displayMode={displayMode}
        isActive={line.isActive}
        deliveryDate={group.deliveryDate}
        returnProductChanged={returnProductChanged}
        hasTracking={hasTracking}
        isInstallationProduct={subLine.isInstallationProduct}
      />
    ));

    return [
      <OrderLine
        key={line.productId}
        line={line}
        displayMode={displayMode}
        isActive={line.isActive}
        deliveryDate={group.deliveryDate}
        returnProductChanged={returnProductChanged}
        hasTracking={hasTracking}
        isInstallationProduct={line.isInstallationProduct}
      />,
      ...subOrderLines
    ];
  });

  return <>{groupLines}</>;
};

const isGroupContainingOnlyRentalProducts = (group: CustomerPageOrderGroup): boolean => {
  return group.lines.every(item => item.isRentalProduct);
};

export default function DeliveryGroup({
  group,
  displayMode,
  returnProductChanged
}: DeliveryGroupProps) {
  const isRentalGroup = isGroupContainingOnlyRentalProducts(group);

  return (
    <div className={styles.deliveryGroup}>
      <div>
        <strong>{group.deliveryName}</strong>
        <span className={styles.shipmentInfo}>
          {!isRentalGroup &&
            group.carriageMethod > 0 &&
            group.lines.some(l => l.isActive) &&
            mapCarriageMethod(group.carriageMethod)}

          {isRentalGroup && rm('delivery_salestatus_rental')}
        </span>
        {GetShipMentInfo(
          group.lines.every(l => !l.isActive),
          group.trackingInformation
        )}
      </div>
      <div className={styles.orderLines}>
        <OrderLines
          hasTracking={
            group.trackingInformation === undefined ? false : group.trackingInformation.length > 0
          }
          displayMode={displayMode}
          returnProductChanged={returnProductChanged}
          group={group}
        />
      </div>
    </div>
  );
}
